import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { useViewport } from '../hooks/useViewport';

interface LimitReachedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCompleteRegistration: () => void;
  apiBase: string;
  hasEmail?: boolean; // If user already has email from Path B registration
  allowClose?: boolean; // If true, modal can be closed (for "Complete Registration" button). If false, user must complete registration (for 7-game limit).
  title?: string; // Custom title (default: "Continue Playing")
}

export default function LimitReachedModal({ 
  isOpen, 
  onClose, 
  onCompleteRegistration, 
  apiBase,
  hasEmail = false,
  allowClose = false,
  title = "Continue Playing"
}: LimitReachedModalProps) {
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobile = viewport.isMobile;
  const isAndroid = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform() === 'android';
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  
  // Real-time password mismatch validation
  const passwordMismatch = password && confirmPassword && password !== confirmPassword;

  const handleResendVerification = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiBase}/email/verify/send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error('Failed to send verification email');
      }

      // Success - close modal
      // Account will be refreshed when user returns to tab after verification (via visibility listener)
      setError(null);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // This function should only be called when hasEmail is false
    if (hasEmail) {
      return;
    }

    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }
    if (!password) {
      setError('Please enter a password');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      // Get guest token for authentication
      const { getValidGuestToken } = await import('../lib/guest-auth');
      const token = await getValidGuestToken();
      
      if (!token) {
        throw new Error('Not authenticated. Please refresh the page.');
      }

      // Complete registration (add email + password)
      const response = await fetch(`${apiBase}/auth/complete-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          email: email.trim(),
          password: password,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMsg = 'Failed to complete registration';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.detail) {
            if (typeof errorData.detail === 'string') {
              errorMsg = errorData.detail;
            } else if (errorData.detail.error) {
              errorMsg = errorData.detail.error;
            }
          }
        } catch (parseErr) {
          errorMsg = errorText || 'Failed to complete registration';
        }
        throw new Error(errorMsg);
      }

      // Success - close and complete registration (onCompleteRegistration will refresh account)
      onCompleteRegistration();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to complete registration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-start justify-center p-4"
          style={{ 
            background: 'rgba(0, 0, 0, 0.7)', 
            paddingTop: isAndroid && isMobile ? 'calc(env(safe-area-inset-top, 0px) + 1rem)' : (isMobile ? '1rem' : '10vh'),
            paddingBottom: isAndroid && isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' : (isMobile ? '1rem' : '1rem'),
            padding: isAndroid && isMobile ? '1rem' : (isMobile ? '1rem' : '1rem')
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && allowClose) {
              onClose();
            }
          }}
        >
          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="relative w-full max-w-md rounded-2xl border-4"
            style={{
              background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
              borderColor: '#8b6542',
              boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
              padding: isAndroid && isMobile ? '0.75rem' : (isMobile ? '1rem' : '1.5rem'),
              maxHeight: isMobile ? 'calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 2rem)' : '90vh',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {allowClose && (
              <button
                onClick={onClose}
                className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full text-2xl font-bold leading-none text-gray-600 hover:text-white hover:bg-gray-800 transition-colors cursor-pointer"
                style={{
                  right: isMobile ? '0.5rem' : '0.5rem',
                  top: isMobile ? '0.5rem' : '0.5rem',
                  width: isMobile ? '1.5rem' : '2rem',
                  height: isMobile ? '1.5rem' : '2rem',
                  fontSize: isMobile ? '1.25rem' : '1.5rem'
                }}
                aria-label="Close"
              >
                ×
              </button>
            )}
            <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} md:text-3xl font-black`} style={{ color: '#3e2615', marginBottom: isMobile ? '0.75rem' : '1rem' }}>
              {title}
            </h2>
            {(!allowClose || allowClose) && (
              <p className={`${isMobile ? 'text-sm mb-4' : 'text-base mb-6'}`} style={{ color: '#3e2615' }}>
                Finish signing up to continue playing, save your progress, and appear on leaderboards.
              </p>
            )}

            {hasEmail ? (
              <div className="flex flex-col" style={{ gap: isMobile ? '0.75rem' : '1rem', marginTop: 'auto' }}>
                <p className={isMobile ? 'text-sm' : 'text-base'} style={{ color: '#3e2615' }}>
                  Please verify your email to continue playing. Check your inbox for the verification link.
                </p>
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={loading}
                  className="w-full rounded-lg font-bold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: '#fffaeb',
                    color: '#3e2615',
                    border: '2px solid #8b6542',
                    boxShadow: '0 4px 0 #8b6542',
                    padding: isMobile ? '0.75rem 1rem' : '0.75rem 1.5rem',
                    fontSize: isMobile ? '0.875rem' : '1.125rem'
                  }}
                >
                  {loading ? 'Sending...' : 'Resend Verification Email'}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col" style={{ gap: isMobile ? '0.75rem' : '1rem', marginTop: 'auto' }}>
                <div>
                  <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium`} style={{ color: '#3e2615', marginBottom: isMobile ? '0.375rem' : '0.5rem' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(null);
                    }}
                    placeholder="your@email.com"
                    className="w-full rounded-xl outline-none transition-all"
                    style={{
                      border: '2px solid #c4a07a',
                      background: '#ffffff',
                      color: '#3e2615',
                      padding: isMobile ? '0.625rem' : '0.75rem',
                      fontSize: isMobile ? '0.875rem' : '1rem'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#d4a05f'}
                    onBlur={(e) => e.target.style.borderColor = '#c4a07a'}
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium`} style={{ color: '#3e2615', marginBottom: isMobile ? '0.375rem' : '0.5rem' }}>
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordTouched(true);
                      setError(null);
                    }}
                    placeholder="At least 8 characters"
                    className="w-full rounded-xl outline-none transition-all"
                    style={{
                      border: '2px solid #c4a07a',
                      background: '#ffffff',
                      color: '#3e2615',
                      padding: isMobile ? '0.625rem' : '0.75rem',
                      fontSize: isMobile ? '0.875rem' : '1rem'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#d4a05f'}
                    onBlur={(e) => {
                      setPasswordTouched(true);
                      e.target.style.borderColor = '#c4a07a';
                    }}
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium`} style={{ color: '#3e2615', marginBottom: isMobile ? '0.375rem' : '0.5rem' }}>
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setConfirmPasswordTouched(true);
                    }}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                    onBlur={() => {
                      setConfirmPasswordTouched(true);
                    }}
                    placeholder="Confirm your password"
                    className="w-full rounded-xl outline-none transition-all"
                    style={{
                      border: '2px solid',
                      borderColor: passwordMismatch && confirmPasswordTouched ? '#d32f2f' : '#c4a07a',
                      background: '#ffffff',
                      color: '#3e2615',
                      padding: isMobile ? '0.625rem' : '0.75rem',
                      fontSize: isMobile ? '0.875rem' : '1rem'
                    }}
                    disabled={loading}
                    autoComplete="new-password"
                    readOnly={false}
                  />
                  {passwordMismatch && confirmPasswordTouched && (
                    <p className={`${isMobile ? 'text-xs mt-1' : 'text-sm mt-2'}`} style={{ color: '#d32f2f' }}>
                      Passwords do not match
                    </p>
                  )}
                </div>

                {error && (
                  <p className={isMobile ? 'text-xs' : 'text-sm'} style={{ color: '#d32f2f' }}>
                    {error}
                  </p>
                )}

                <div className="flex flex-col" style={{ gap: isMobile ? '0.5rem' : '0.75rem', marginTop: 'auto' }}>
                  <button
                    type="submit"
                    disabled={loading || (passwordMismatch && confirmPasswordTouched)}
                    className="w-full rounded-lg font-bold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: '#fffaeb',
                      color: '#3e2615',
                      border: '2px solid #8b6542',
                      boxShadow: '0 4px 0 #8b6542',
                      padding: isMobile ? '0.75rem 1rem' : '0.75rem 1.5rem',
                      fontSize: isMobile ? '0.875rem' : '1.125rem'
                    }}
                  >
                    {loading ? 'Creating Account...' : 'Create Free Account'}
                  </button>
                </div>

                <p className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-center`} style={{ color: '#3e2615', opacity: 0.7 }}>
                  Free registration. We just need a verified email to continue playing.
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
